import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, ConfigProvider, Input, Modal, Popover, Select, Switch, Table, Tag, Tooltip, message, theme } from 'antd';
import { ArrowsCounterClockwiseIcon, ColumnsIcon, MagnifyingGlassIcon, PlugsIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import useAccounts, { useIsDark, useNarrow } from './useAccounts';
import AccountDrawer from './AccountDrawer';
import { LiveIndicator, ProviderIcon, StatusTag, StoreBanner, TestResult, expiryText } from './AccountParts';
import { OPS, STATUS, STATUS_ORDER, categoryOf, searchText, typeFiltersFor, typeKey, typeLabel } from './accountTypes';
import { disconnectAccount, makeDefault, testAccount } from '../../services/AccountsService';
import { formatRelativeTime } from '../../utils/relativeTime';
import VendorName from '../sandbox/VendorName';
import { useHiddenColumns } from '../workspaces/ProjectListTools';
import './accounts.css';

const projectLabel = (use) => use.projectName || use.projectId;

/** Who the account is: `host · login` for git and tools; the username and registry URL for a registry. */
function identity(account, category) {
  if (category === 'registry') {
    return { primary: account.username || account.login || 'token only', secondary: account.baseUrl || account.host };
  }
  return { primary: `${account.host} · ${account.username || account.login}`, secondary: null };
}

// The projects shown as chips in "Used by"; the rest behind "+N", whose popover lists them all.
const USED_BY_CHIPS = 2;

/** "Used by": at most two project chips (each cut to the cell's width) and "+N", which lists every project and repo. */
function UsedBy({ account }) {
  const used = account.usedBy || [];
  if (!used.length) { return <span className="acct-muted">not used</span>; }
  const rest = used.length - USED_BY_CHIPS;
  return (
    <span className="acct-tags acct-used-by">
      {used.slice(0, USED_BY_CHIPS).map((u) => (
        <Tooltip key={`${u.projectId}-${u.repo || ''}`} title={u.repo || u.projectId}>
          <Tag className="acct-chip">{projectLabel(u)}</Tag>
        </Tooltip>
      ))}
      {rest > 0 && (
        <Popover
          content={(
            <ul aria-label={`Projects using ${account.label || account.name}`} className="acct-used-list">
              {used.map((u) => (
                <li key={`${u.projectId}-${u.repo || ''}`}>
                  <strong>{projectLabel(u)}</strong>
                  {u.repo && <code>{u.repo}</code>}
                </li>
              ))}
            </ul>
          )}
          placement="bottomLeft"
          title={`Used by ${used.length} projects`}
          trigger="click"
        >
          <button aria-label={`${rest} more: show all ${used.length} projects`} className="acct-more-chip" type="button">+{rest}</button>
        </Popover>
      )}
    </span>
  );
}

// The columns a person can hide (Columns ▾, remembered in this browser); Name always shows, the actions too.
const COLUMN_CHOICES = [
  { key: 'name', label: 'Name' },
  { key: 'type', label: 'Type' },
  { key: 'identity', label: 'Identifies as' },
  { key: 'status', label: 'Status' },
  { key: 'usedBy', label: 'Used by' },
  { key: 'changed', label: 'Changed' },
  { key: 'used', label: 'Last used' },
];

function RecentUses({ uses }) {
  if (!uses?.length) { return <span className="acct-muted">Not used yet.</span>; }
  return (
    <ul className="acct-uses">
      {uses.map((u) => (
        <li key={`${u.at}-${u.op}-${u.projectId}`}>
          <strong>{OPS[u.op] || u.op}</strong>
          {' '}{projectLabel(u) ? <>in <span>{projectLabel(u)}</span></> : null}
          {u.remote ? <> · {u.remote}</> : null}
          {u.repo ? <> · <code>{u.repo}</code></> : null}
          {u.via ? <> · via <VendorName name={u.via} size={12} /></> : null}
          <span className="acct-muted"> · {formatRelativeTime(u.at)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Features › Connections › Your accounts: the person's git hosting, package registry and tool accounts that Flow, its
 * agents and their AI tool use to clone, pull and push private repositories (and the sandbox to install packages) (docs/git-accounts.md). Same shape as Console Next's
 * Import › Credentials: store health, filters, a table with expandable rows, and an Add/Replace drawer. Metadata only:
 * tokens are write-only and never come back.
 */
export default function YourAccounts() {
  const dark = useIsDark();
  const narrow = useNarrow();
  const [open, setOpen] = useState(null);
  const { data, error, loading, updatedAt, failures, hidden, refresh } = useAccounts();
  const [query, setQuery] = useState('');
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [drawer, setDrawer] = useState(null); // { replace: account|null }
  const [testing, setTesting] = useState(null);
  const [lastTest, setLastTest] = useState(null);
  const [linked, setLinked] = useState(false);
  const columnChoice = useHiddenColumns('flow.yourAccounts.hiddenColumns', COLUMN_CHOICES);

  // Back from a provider's OAuth consent (returnUri): say so, read the list, drop the flag from the URL.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('account_linked') === '1') {
      setLinked(true);
      url.searchParams.delete('account_linked');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      refresh();
    }
  }, [refresh]);

  // `/features/connections#your-accounts` (Settings, the global search): this section, in view.
  useEffect(() => {
    if (window.location.hash !== '#your-accounts') { return undefined; }
    const timer = setTimeout(() => document.getElementById('your-accounts')?.scrollIntoView?.({ block: 'start' }), 60);
    return () => clearTimeout(timer);
  }, []);

  const off = error?.code === 'accounts_off' || (error?.status === 404 && !data);
  const storeState = off ? 'off' : (error && !data ? 'unreachable' : data?.store?.state || (loading ? null : 'unreachable'));
  const usable = storeState === 'connected';
  const accounts = data?.accounts || [];
  const providers = data?.providers;
  const catalog = data?.catalog || null;
  const catalogLabel = (a) => {
    const info = catalog?.find((i) => i.type === a.type);
    return typeLabel(a, info);
  };
  const typeFilters = useMemo(() => typeFiltersFor(accounts, catalog), [accounts, catalog]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return accounts.filter((a) => (!q || searchText(a).includes(q))
      && (!types.length || types.includes(typeKey(a)))
      && (!statuses.length || statuses.includes(a.status)));
  }, [accounts, query, types, statuses]);

  const runTest = async(account) => {
    setTesting(account.id);
    try {
      const answer = await testAccount(account.id);
      setLastTest({ label: answer.account.label, outcome: answer.test });
      refresh();
    } catch (err) {
      setLastTest({ label: account.label, outcome: { ok: false, at: Date.now(), message: err.message } });
    } finally {
      setTesting(null);
    }
  };

  const setDefault = async(account) => {
    try {
      await makeDefault(account.id);
      message.success(`${account.label} is now the default for ${account.host}`);
      refresh();
    } catch (err) {
      message.error(err.message);
    }
  };

  const disconnect = (account) => {
    Modal.confirm({
      title: `Disconnect ${account.label}?`,
      content: (
        <div>
          <p>Flow forgets this account and its token. It is revoked at {account.host} where its API allows; otherwise Flow tells you where to revoke it yourself.</p>
          {(account.usedBy || []).length > 0 && <p>Used by {account.usedBy.map(projectLabel).join(', ')}: their pulls and pushes need another account for {account.host}.</p>}
        </div>
      ),
      okText: 'Disconnect',
      okButtonProps: { danger: true },
      onOk: async() => {
        try {
          const answer = await disconnectAccount(account.id);
          if (answer?.revoked) {
            message.success(`${account.label} disconnected. ${answer.revokeNote || ''}`.trim());
          } else {
            Modal.info({ title: `${account.label} disconnected`, content: answer?.revokeNote || 'Flow no longer has its token.' });
          }
          refresh();
        } catch (err) {
          message.error(err.message);
        }
      },
    });
  };

  const columns = [
    {
      // No width: Name takes what the others leave (the table is laid out fixed, so nothing overflows into the next cell).
      key: 'name',
      title: 'Name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (_, a) => (
        <span className="acct-name">
          <span className="acct-icon-box"><ProviderIcon category={categoryOf(a.type, catalog)} type={a.type} /></span>
          <span className="acct-name-text">
            <span className="acct-name-primary">{a.name}{a.isDefault && <Tag color="blue" style={{ marginInlineStart: 6 }}>Default</Tag>}</span>
            <span className="acct-name-secondary" title={a.storagePath}>{a.storagePath}</span>
          </span>
        </span>
      ),
    },
    { key: 'type', title: 'Type', width: 100, ellipsis: { showTitle: true }, render: (_, a) => catalogLabel(a), sorter: (a, b) => catalogLabel(a).localeCompare(catalogLabel(b)) },
    {
      key: 'identity',
      title: 'Identifies as',
      width: 170,
      sorter: (a, b) => identity(a, categoryOf(a.type, catalog)).primary.localeCompare(identity(b, categoryOf(b.type, catalog)).primary),
      render: (_, a) => {
        const who = identity(a, categoryOf(a.type, catalog));
        return (
          <span className="acct-name-text">
            <span className="acct-cell-line" title={who.primary}>{who.primary}</span>
            {who.secondary && <span className="acct-name-secondary" title={who.secondary}>{who.secondary}</span>}
            {a.last4 && <span className="acct-name-secondary">••••{a.last4}</span>}
          </span>
        );
      },
    },
    {
      key: 'status',
      title: 'Status',
      width: 104,
      render: (_, a) => <StatusTag account={a} />,
      sorter: (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status),
    },
    {
      key: 'usedBy', title: 'Used by', width: 170, render: (_, a) => <UsedBy account={a} />, sorter: (a, b) => (a.usedBy || []).length - (b.usedBy || []).length,
    },
    {
      // When, then who on its own line (cut to the cell, the whole address on hover).
      key: 'changed',
      title: 'Changed',
      width: 130,
      sorter: (a, b) => (a.updatedAt || 0) - (b.updatedAt || 0),
      render: (_, a) => (
        <span className="acct-name-text">
          <span className="acct-cell-line">{formatRelativeTime(a.updatedAt)}</span>
          {a.updatedBy && <span className="acct-name-secondary" title={a.updatedBy}>by {a.updatedBy}</span>}
        </span>
      ),
    },
    {
      key: 'used',
      title: 'Last used',
      width: 100,
      sorter: (a, b) => (a.lastUsed?.at || 0) - (b.lastUsed?.at || 0),
      render: (_, a) => (a.lastUsed
        ? <Tooltip title={`${OPS[a.lastUsed.op] || a.lastUsed.op}${a.lastUsed.projectName || a.lastUsed.projectId ? ` in ${projectLabel(a.lastUsed)}` : ''}${a.lastUsed.remote ? ` · ${a.lastUsed.remote}` : ''}`}><span>{formatRelativeTime(a.lastUsed.at)}</span></Tooltip>
        : <span className="acct-muted">never</span>),
    },
    {
      key: 'actions',
      title: <span className="acct-sr">Actions</span>,
      width: 150,
      fixed: 'right',
      render: (_, a) => (
        <span className="acct-actions">
          <Button aria-label={`Test ${a.label}`} disabled={!usable} icon={<PlugsIcon aria-hidden size={14} />} loading={testing === a.id} onClick={() => runTest(a)} size="small">Test</Button>
          <Tooltip title="Replace the token">
            <Button aria-label={`Replace ${a.label}`} disabled={!usable} icon={<ArrowsCounterClockwiseIcon aria-hidden size={14} />} onClick={() => setDrawer({ replace: a })} size="small" />
          </Tooltip>
          <Tooltip title="Disconnect">
            <Button aria-label={`Disconnect ${a.label}`} danger disabled={!usable} icon={<TrashIcon aria-hidden size={14} />} onClick={() => disconnect(a)} size="small" />
          </Tooltip>
        </span>
      ),
    },
  ];

  const expandedRowRender = (a) => (
    <div className="acct-expanded">
      <div className="acct-expanded-row">
        <span className="acct-expanded-label">Scopes</span>
        <span className="acct-tags">{a.scopes?.length ? a.scopes.map((s) => <Tag key={s} style={{ marginInlineEnd: 0 }}>{s}</Tag>) : <span className="acct-muted">{a.type === 'github' && a.method === 'token' ? 'Fine-grained token: permissions are per repository' : 'Not reported'}</span>}</span>
      </div>
      {a.options && Object.keys(a.options).length > 0 && (
        <div className="acct-expanded-row">
          <span className="acct-expanded-label">Settings</span>
          <span className="acct-tags">
            {Object.entries(a.options).map(([k, v]) => <Tag key={k} style={{ marginInlineEnd: 0 }}>{typeof v === 'boolean' ? `${k}: ${v ? 'on' : 'off'}` : `${k}: ${v}`}</Tag>)}
          </span>
        </div>
      )}
      <div className="acct-expanded-row">
        <span className="acct-expanded-label">Expiry</span>
        <span>{expiryText(a)}</span>
      </div>
      <div className="acct-expanded-row">
        <span className="acct-expanded-label">Default for {a.host}</span>
        <span className="acct-default">
          <Switch aria-label={`Default account for ${a.host}`} checked={a.isDefault} disabled={a.isDefault || !usable} onChange={() => setDefault(a)} size="small" />
          <span className="acct-muted">{a.isDefault ? 'Used when no account is named. One account per host is the default.' : 'Make it the one used when no account is named.'}</span>
        </span>
      </div>
      {a.lastTest && (
        <div className="acct-expanded-row">
          <span className="acct-expanded-label">Last test</span>
          <span>{a.lastTest.ok ? 'Passed' : 'Failed'} {formatRelativeTime(a.lastTest.at)}: {a.lastTest.message}</span>
        </div>
      )}
      <div className="acct-expanded-row">
        <span className="acct-expanded-label">Recent uses</span>
        <RecentUses uses={a.recentUses} />
      </div>
    </div>
  );

  const empty = storeState && !usable
    ? 'Your accounts store is not usable: see above.'
    : 'No accounts yet: connect your git hosting (GitHub, GitLab, …) so Flow, its agents and your AI tools can clone, pull and push your private repositories with your own account, or a package registry for installs in your sandbox.';

  return (
    <ConfigProvider theme={{ algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
      <section aria-labelledby="your-accounts-title" className="sbx-card acct-section" id="your-accounts">
        <div className="acct-head">
          <div className="acct-head-text">
            <h2 id="your-accounts-title">Your accounts</h2>
            <p>Your git hosting, package registry and tool accounts, kept in OpenBao. Tokens are write-only: never shown again, never sent to the browser or to AI models. Flow, its agents and your AI tool use them to clone, pull and push your private repositories, and your sandbox to install private packages.</p>
          </div>
          <div className="acct-head-actions">
            {!off && <LiveIndicator failures={failures} hidden={hidden} onRefresh={refresh} updatedAt={updatedAt} />}
            <Button disabled={!usable} icon={<PlusIcon aria-hidden size={16} />} onClick={() => setDrawer({ replace: null })} type="primary">Add account</Button>
          </div>
        </div>

        {linked && <TestResult onClose={() => setLinked(false)} outcome={{ ok: true, message: 'The account is linked: it shows in the list below.' }} title="Account connected" />}
        {storeState && (
          <StoreBanner
            detail={off ? 'Flow has no accounts service in this environment.' : (storeState === 'unreachable' && error ? error.message : data?.store?.detail)}
            fix={off ? 'Ask your admin to enable providerTokenService and openbao in the Flow chart (docs/git-accounts.md).' : data?.store?.fix}
            state={storeState}
          />
        )}
        {lastTest && <TestResult onClose={() => setLastTest(null)} outcome={lastTest.outcome} title={`${lastTest.label}: ${lastTest.outcome.ok ? (lastTest.outcome.hint ? 'connected, with a least-privilege warning' : 'connected') : 'test failed'}`} />}

        {!off && !data && loading && <p className="acct-muted">Loading your accounts…</p>}
        {!off && data && (
          <>
            <div className="acct-filters">
              <Input
                allowClear
                aria-label="Search accounts"
                className="acct-search"
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, identity, usage"
                prefix={<MagnifyingGlassIcon aria-hidden size={14} />}
                value={query}
              />
              <Select aria-label="Filter by type" className="acct-filter" mode="multiple" onChange={setTypes} options={typeFilters} placeholder="All types" value={types} />
              <Select aria-label="Filter by status" className="acct-filter" mode="multiple" onChange={setStatuses} options={STATUS_ORDER.map((s) => ({ value: s, label: STATUS[s].label }))} placeholder="Any status" value={statuses} />
              <span className="acct-count">{rows.length} of {accounts.length}</span>
              {!narrow && (
                <Popover
                  content={(
                    <fieldset className="acct-columns" data-testid="acct-columns-menu">
                      <legend className="acct-sr">Columns to show</legend>
                      {COLUMN_CHOICES.slice(1).map((column) => (
                        <Checkbox checked={columnChoice.show(column.key)} key={column.key} onChange={() => columnChoice.toggle(column.key)}>{column.label}</Checkbox>
                      ))}
                    </fieldset>
                  )}
                  placement="bottomRight"
                  trigger="click"
                >
                  <Button aria-haspopup="true" icon={<ColumnsIcon aria-hidden size={14} />}>Columns</Button>
                </Popover>
              )}
            </div>
            {narrow ? (
              <ul aria-label="Your accounts" className="acct-cards">
                {rows.length === 0 && <li className="acct-muted">{empty}</li>}
                {rows.map((a) => (
                  <li className="acct-card" key={a.id}>
                    <div className="acct-card-head">
                      {columns[0].render(null, a)}
                      <StatusTag account={a} />
                    </div>
                    <div className="acct-card-meta">
                      <span>{catalogLabel(a)}</span>
                      <span>{[identity(a, categoryOf(a.type, catalog)).primary, identity(a, categoryOf(a.type, catalog)).secondary, a.last4 ? `••••${a.last4}` : null].filter(Boolean).join(' · ')}</span>
                      <span>Last used: {a.lastUsed ? formatRelativeTime(a.lastUsed.at) : 'never'} · Changed {formatRelativeTime(a.updatedAt)}</span>
                      <UsedBy account={a} />
                    </div>
                    <div className="acct-card-actions">
                      {columns[columns.length - 1].render(null, a)}
                      <Button aria-expanded={open === a.id} onClick={() => setOpen(open === a.id ? null : a.id)} size="small" type="link">{open === a.id ? 'Less' : 'Details'}</Button>
                    </div>
                    {open === a.id && expandedRowRender(a)}
                  </li>
                ))}
              </ul>
            ) : (
            <div className="acct-table">
              {/* Laid out fixed: each cell keeps its width and cuts what does not fit; the table scrolls sideways (the
                  actions staying in sight) only when the box is narrower than its columns. */}
              <Table
                columns={columns.filter((column) => column.key === 'actions' || columnChoice.show(column.key))}
                dataSource={rows}
                expandable={{ expandedRowRender }}
                locale={{ emptyText: empty }}
                pagination={false}
                rowKey="id"
                scroll={{ x: 960 }}
                size="small"
                tableLayout="fixed"
              />
            </div>
            )}
          </>
        )}

        <AccountDrawer
          disabled={!usable}
          onClose={() => setDrawer(null)}
          onSaved={refresh}
          open={Boolean(drawer)}
          catalog={catalog}
          providers={providers}
          replace={drawer?.replace || null}
        />
      </section>
    </ConfigProvider>
  );
}
